import { publicOutboundRateBullets, publicReturnCostBullets, shippingPolicyDecision } from './shippingRates';

export type LegalSection = {
  heading: string;
  body?: string[];
  bullets?: string[];
};

export type LegalPage = {
  slug: string;
  title: string;
  navLabel: string;
  summary: string;
  updated: string;
  sections: LegalSection[];
};

export const operatorDetails = {
  brand: 'ProPickleball.nl',
  legalOwner: 'Klokkers Consultancy',
  operatingGroup: 'Sevensa',
  address: 'Vierbanse Gantel 38, 5032 CL Tilburg, Nederland',
  kvk: '91968429',
  vat: 'NL004928587B17',
  email: 'info@propickleball.nl',
  phone: '+31 6 19200380',
  returnAddress: 'Vierbanse Gantel 38, 5032 CL Tilburg, Nederland'
} as const;

const updated = '4 juli 2026';

const launchStatus =
  'ProPickleball.nl is ingericht als specialistische webshop voor Nederland en Belgie. Je kunt het assortiment bekijken, producten vergelijken, de keuzehulp gebruiken en advies of een clubofferte aanvragen. Online bestellen blijft alleen open voor producten waarvoor prijs, voorraad, leverancier, verzending, retourroute en productveiligheidsinformatie zijn gecontroleerd.';

export const legalPages: LegalPage[] = [
  {
    slug: '/algemene-voorwaarden/',
    title: 'Algemene voorwaarden',
    navLabel: 'Voorwaarden',
    summary: 'De afspraken voor advies, offerteaanvragen en bestellingen via ProPickleball.nl.',
    updated,
    sections: [
      {
        heading: 'Wie is de verkoper?',
        body: [
          `${operatorDetails.brand} wordt gevoerd door ${operatorDetails.legalOwner}, onderdeel van ${operatorDetails.operatingGroup}. Ons adres is ${operatorDetails.address}. KVK: ${operatorDetails.kvk}. BTW-id: ${operatorDetails.vat}. E-mail: ${operatorDetails.email}. Telefoon/WhatsApp: ${operatorDetails.phone}.`
        ]
      },
      {
        heading: 'Wanneer gelden deze voorwaarden?',
        body: [
          launchStatus,
          'Deze voorwaarden gelden voor het gebruik van de site, adviesaanvragen, offerteaanvragen en consumentenbestellingen via ProPickleball.nl. Voor zakelijke club-, school- of verenigingspakketten kunnen aanvullende afspraken in de offerte staan.'
        ]
      },
      {
        heading: 'Aanbod, prijzen en productinformatie',
        bullets: [
          'We proberen productinformatie duidelijk, actueel en praktisch te houden. Een duidelijke vergissing, typefout of technische fout bindt ons niet.',
          'Consumentenprijzen worden inclusief btw getoond wanneer een product bestelbaar is.',
          'Voorraad, levertijd, merkautorisatie, keurmerken, toernooigoedkeuringen en materiaalclaims tonen we alleen wanneer de leverancier, distributeur of fabrikant dit voldoende ondersteunt.',
          'Een wachtlijstinschrijving, adviesaanvraag of offerteaanvraag is geen bestelling en verplicht je niet tot aankoop.'
        ]
      },
      {
        heading: 'Bestellen en betalen',
        body: [
          'Een bestelling komt pas tot stand nadat je de order bevestigt, de betaalstap succesvol afrondt en wij de order accepteren. Betaling loopt server-side via de betaalprovider, met Mollie als beoogde PSP.',
          'Voor betaling tonen we de belangrijkste informatie: product, prijs, btw, verzendkosten, retourinformatie, verwachte levering en eventuele product- of maatwerkbeperkingen.'
        ]
      },
      {
        heading: 'Levering',
        body: [
          'De verwachte levertijd staat bij checkout of in de offerte. Als geen levertijd is afgesproken, geldt de wettelijke leveringstermijn van 30 dagen. Is levering niet haalbaar, dan informeren we je en zoeken we een oplossing of annuleren we de order volgens de wettelijke regels.',
          'Het transportrisico blijft bij ProPickleball.nl tot jij het product hebt ontvangen, behalve wanneer je zelf een vervoerder kiest die wij niet hebben aangeboden.'
        ]
      },
      {
        heading: 'Bedenktijd en retouren',
        body: [
          `Bij online consumentenbestellingen heb je meestal 14 dagen bedenktijd na ontvangst. Na herroeping stuur je het product binnen 14 dagen terug naar ${operatorDetails.returnAddress}, tenzij support schriftelijk een andere retourroute bevestigt.`,
          'Retour aanmelden kan via /retour-aanmelden/ of per e-mail. Alleen het product terugsturen is niet genoeg; laat ons eerst duidelijk weten dat je de koop wilt herroepen.'
        ],
        bullets: [
          'Bij volledige herroeping betalen we de aankoopprijs en standaard leverkosten terug.',
          'Retourkosten zijn voor de consument wanneer dit voor aankoop duidelijk is gemeld.',
          'We betalen uiterlijk binnen 14 dagen na herroeping terug. We mogen wachten totdat het product terug is of totdat je retourbewijs hebt gestuurd.',
          'Gebruik je het product verder dan nodig om aard, kenmerken en werking te beoordelen, dan kan waardevermindering worden verrekend.'
        ]
      },
      {
        heading: 'Garantie en klachten',
        bullets: [
          'Je hebt recht op een product dat doet wat je er redelijkerwijs van mag verwachten.',
          'Een eventuele fabrieks- of merkgarantie beperkt je wettelijke rechten niet.',
          `Mail bij een klacht of defect naar ${operatorDetails.email} met ordernummer, productnaam, beschrijving en foto of video.`,
          'We reageren uiterlijk binnen 14 dagen inhoudelijk of met een concrete voortgangsupdate.'
        ]
      },
      {
        heading: 'Veilig gebruik',
        body: [
          'Gebruik paddles, ballen, netten, lijnen, tassen en accessoires alleen waarvoor ze bedoeld zijn. Volg waarschuwingen, handleidingen en montage-instructies. Meld een mogelijk veiligheidsprobleem direct via info@propickleball.nl.'
        ]
      },
      {
        heading: 'Aansprakelijkheid en recht',
        body: [
          'We sluiten verplichte consumentenrechten niet uit. Voor zover de wet dat toestaat, is onze aansprakelijkheid beperkt tot directe schade die redelijkerwijs voorzienbaar was en verband houdt met de betreffende order of aanvraag.',
          'Op deze voorwaarden is Nederlands recht van toepassing. Dwingende consumentenbescherming in Nederland of Belgie blijft gelden.'
        ]
      }
    ]
  },
  {
    slug: '/privacybeleid/',
    title: 'Privacybeleid',
    navLabel: 'Privacy',
    summary: 'Welke gegevens we gebruiken voor advies, offertes, service, retouren en toekomstige bestellingen.',
    updated,
    sections: [
      {
        heading: 'Wie is verantwoordelijk?',
        body: [
          `${operatorDetails.legalOwner} is de verwerkingsverantwoordelijke voor persoonsgegevens die via ${operatorDetails.brand} worden verwerkt. Je kunt ons bereiken via ${operatorDetails.email}, ${operatorDetails.phone} en ${operatorDetails.address}.`
        ]
      },
      {
        heading: 'Welke gegevens gebruiken we?',
        bullets: [
          'Contactgegevens zoals naam, e-mailadres, telefoonnummer en berichtinhoud.',
          'Adviesgegevens zoals speelervaring, productinteresse, club/school/bedrijf, budget, gewenste aantallen en gebruikssituatie.',
          'Bestel-, betaal-, factuur-, retour- en garantiegegevens zodra online bestellen actief is.',
          'Technische gegevens zoals browser, apparaat, consentstatus, beveiligingslogs en foutmeldingen die nodig zijn voor werking en beveiliging.'
        ]
      },
      {
        heading: 'Waarom gebruiken we gegevens?',
        bullets: [
          'Om je adviesvraag, offerteaanvraag, retourvraag of klacht te beantwoorden.',
          'Om club-, school- of verenigingspakketten samen te stellen.',
          'Om bestellingen, betalingen, verzending, retouren en garantie te verwerken zodra checkout actief is.',
          'Om wettelijke administratie, fiscale bewaarplicht, productveiligheid en fraudepreventie uit te voeren.',
          'Om nieuwsbrieven, wachtlijstupdates, analytics of marketing alleen te gebruiken waar dat mag en waar toestemming nodig is.'
        ]
      },
      {
        heading: 'Grondslagen',
        bullets: [
          'Uitvoering van een overeenkomst of precontractueel verzoek voor advies, offerte en bestelling.',
          'Wettelijke verplichting voor administratie, btw, boekhouding en productveiligheid.',
          'Gerechtvaardigd belang voor beveiliging, serviceopvolging en basisbedrijfsvoering.',
          'Toestemming voor nieuwsbrief, marketingcookies en tracking waar toestemming vereist is.'
        ]
      },
      {
        heading: 'Bewaartermijnen',
        body: [
          'Advies-, wachtlijst- en offerteaanvragen bewaren we maximaal 24 maanden na het laatste contact, tenzij je eerder verwijdering vraagt of langere bewaring nodig is voor een geschil of veiligheidsmelding. Administratieve gegevens van bestellingen bewaren we in beginsel 7 jaar vanwege fiscale verplichtingen.'
        ]
      },
      {
        heading: 'Met wie delen we gegevens?',
        body: [
          'Alleen met partijen die nodig zijn voor hosting, e-mail, CRM, betaling, verzending, boekhouding, klantenservice, security of analytics. We delen niet meer gegevens dan nodig en gebruiken verwerkersafspraken waar dat vereist is.'
        ]
      },
      {
        heading: 'Jouw rechten',
        body: [
          `Je kunt vragen om inzage, correctie, verwijdering, beperking, overdraagbaarheid of bezwaar via ${operatorDetails.email}. Je kunt toestemming intrekken via de cookiebanner of door contact op te nemen. Je kunt ook een klacht indienen bij de Autoriteit Persoonsgegevens.`
        ]
      }
    ]
  },
  {
    slug: '/cookiebeleid/',
    title: 'Cookiebeleid',
    navLabel: 'Cookies',
    summary: 'Welke cookies en toestemmingskeuzes ProPickleball.nl gebruikt.',
    updated,
    sections: [
      {
        heading: 'Kort uitgelegd',
        body: [
          'We gebruiken noodzakelijke opslag voor de werking van de site en om je cookievoorkeuren te onthouden. Analytics en marketing staan standaard uit totdat je daarvoor kiest, tenzij een toekomstige analytics-inrichting aantoonbaar privacyvriendelijk en wettelijk toegestaan is zonder toestemming.'
        ]
      },
      {
        heading: 'Categorieen',
        bullets: [
          'Noodzakelijk: nodig voor werking, beveiliging, voorkeuren en basisformulieren. Deze categorie kan niet uit.',
          'Analytics: helpt ons begrijpen welke pagina\'s en flows beter moeten. Alleen actief na toestemming of een wettelijk passende privacyvriendelijke inrichting.',
          'Marketing: advertentie- of remarketingcookies. Alleen actief na expliciete toestemming.'
        ]
      },
      {
        heading: 'Je keuze wijzigen',
        body: [
          'Je kunt cookies weigeren, geselecteerde categorieen opslaan of alles accepteren in de cookiebanner. Je kunt je keuze later wijzigen met de knop Cookievoorkeuren in de footer.'
        ]
      },
      {
        heading: 'Geen cookiewall',
        body: [
          'Je kunt de site blijven gebruiken wanneer je analytics of marketing weigert. Doorgaan met browsen is geen toestemming en opties worden niet vooraf aangevinkt.'
        ]
      }
    ]
  },
  {
    slug: '/levering-en-verzending/',
    title: 'Levering en verzending',
    navLabel: 'Levering',
    summary: 'Hoe verzending voor Nederland en Belgie wordt ingericht.',
    updated,
    sections: [
      {
        heading: 'Wat kun je verwachten?',
        body: [
          launchStatus,
          'Voor elk bestelbaar product tonen we voor betaling de verzendmethode, verzendkosten, verwachte levertijd en eventuele beperkingen. Bij clubpakketten, netsystemen en grote combinaties kiezen we liever voor offerte dan voor een onduidelijk standaardtarief.'
        ]
      },
      {
        heading: 'Productklassen',
        bullets: [
          'Pakketpost: paddles, ballen, grips, tape, kleine accessoires en tassen.',
          'Groot pakket of offerte: portable nets, belijning, startersets met net en club- of schoolpakketten.',
          'Schoenen en apparel: alleen wanneer leverancier, maatvoering en retourafspraken duidelijk zijn.',
          'Belgie: alleen actief wanneer btw/OSS, verzendtarief, retourroute en betaling operationeel zijn.'
        ]
      },
      {
        heading: 'Standaard verzendtarieven voor verkoopstart',
        body: [
          `PostNL is gekozen als basis voor de eerste NL/BE verzendtabel. De tabel is gecontroleerd op ${shippingPolicyDecision.sourceCheckedAt} en wordt opnieuw bekeken rond ${shippingPolicyDecision.nextTariffReview}.`,
          'We gebruiken een herkenbare webshopaanpak: vaste bijdrage onder de drempel, gratis verzending boven de drempel en maatwerk voor grote pakketten.'
        ],
        bullets: publicOutboundRateBullets
      },
      {
        heading: 'Levertijd en tracking',
        body: [
          'Bij live verkoop staat de verwachte levertijd voor betaling op de site. Zodra een zending is aangemaakt, delen we trackinginformatie als de vervoerder die beschikbaar stelt. Als levering vertraagt, informeren we je en blijft annulering mogelijk volgens de wettelijke regels.'
        ]
      },
      {
        heading: 'Schade of ontbrekende onderdelen',
        bullets: [
          'Controleer je pakket kort na ontvangst.',
          `Meld schade, ontbrekende onderdelen of verkeerde levering via ${operatorDetails.email}.`,
          'Stuur foto\'s van de verpakking, het verzendlabel, het product en de schade mee, zodat we snel met vervoerder of leverancier kunnen schakelen.'
        ]
      }
    ]
  },
  {
    slug: '/retouren/',
    title: 'Retouren en herroepingsrecht',
    navLabel: 'Retouren',
    summary: 'Zo meld je een retour, herroeping of ruilvraag aan.',
    updated,
    sections: [
      {
        heading: '14 dagen bedenktijd',
        body: [
          'Bij online consumentenbestellingen heb je meestal 14 dagen bedenktijd vanaf de dag nadat je het product hebt ontvangen. Je hoeft geen reden te geven. Meld je herroeping binnen die termijn via /retour-aanmelden/ of per e-mail.',
          'Na je melding stuur je het product binnen 14 dagen terug. Voor zakelijke offertes, clubpakketten, maatwerk of speciaal ingekochte combinaties kunnen afwijkende afspraken gelden wanneer dat vooraf duidelijk is afgesproken.'
        ]
      },
      {
        heading: 'Retour aanmelden',
        body: [
          `Gebruik /retour-aanmelden/ of mail ${operatorDetails.email} met ordernummer, naam, e-mailadres, product(en), ontvangstdatum en je vraag. Je krijgt daarna de retourinstructie en bevestiging van de route.`,
          `Retouradres: ${operatorDetails.returnAddress}. Stuur producten pas terug nadat je retourroute is bevestigd, vooral bij netsystemen, grote pakketten of clubpakketten.`
        ]
      },
      {
        heading: 'Kosten en terugbetaling',
        bullets: [
          'Retourkosten zijn voor jou wanneer dit voor aankoop duidelijk is gemeld.',
          'Bij volledige herroeping betalen we de aankoopprijs en de standaard leverkosten terug.',
          'Een duurdere verzendoptie dan standaardlevering hoeven we niet extra te vergoeden.',
          'Terugbetaling gebeurt uiterlijk binnen 14 dagen na je herroepingsmelding. We mogen wachten tot het product terug is of tot je retourbewijs hebt gestuurd.'
        ]
      },
      {
        heading: 'Standaard retourkosten voor verkoopstart',
        body: [
          'Onderstaande bedragen zijn de standaard retourkostentabel voor gewone pakketten. Grote pakketten, netsystemen en clubpakketten blijven quote-first totdat carrier- en verpakkingsbewijs compleet is.'
        ],
        bullets: publicReturnCostBullets
      },
      {
        heading: 'Productconditie',
        bullets: [
          'Stuur producten compleet, schoon en zorgvuldig verpakt terug.',
          'Gebruik waar mogelijk de originele verpakking, labels, handleidingen, tasjes, haringen en losse onderdelen.',
          'Je mag het product bekijken en testen zoals in een winkel. Gebruik dat verder gaat dan nodig kan leiden tot waardevermindering.',
          'Hygienische of sterk gebruikte producten kunnen extra beoordeling vragen, binnen de wettelijke grenzen.'
        ]
      }
    ]
  },
  {
    slug: '/garantie/',
    title: 'Garantie en klachten',
    navLabel: 'Garantie',
    summary: 'Wat je kunt doen bij een defect, klacht of garantiepunt.',
    updated,
    sections: [
      {
        heading: 'Wettelijke garantie',
        body: [
          'Je hebt recht op een goed product. Een product moet passen bij de overeenkomst, de productinformatie, de prijs, normaal gebruik en wat je redelijkerwijs mag verwachten. Dit heet wettelijke conformiteit.'
        ]
      },
      {
        heading: 'Merk- of fabrieksgarantie',
        body: [
          'Sommige merken of leveranciers bieden extra garantie. We noemen die garantie pas op een productpagina wanneer de voorwaarden bevestigd zijn. Extra garantie beperkt je wettelijke rechten niet.'
        ]
      },
      {
        heading: 'Defect of klacht melden',
        bullets: [
          `Mail ${operatorDetails.email} met ordernummer, productnaam, beschrijving van het probleem, foto of video, datum van eerste gebruik en informatie over eventuele transportschade.`,
          'We beoordelen of het gaat om transportschade, een productdefect, normale slijtage, verkeerd gebruik of een leveranciersclaim.',
          'Bij een gegronde klacht zoeken we een passende oplossing, zoals herstel, vervanging, prijsvermindering of terugbetaling.',
          'Je ontvangt uiterlijk binnen 14 dagen een inhoudelijke reactie of een concrete voortgangsupdate.'
        ]
      },
      {
        heading: 'Eerlijke claims',
        body: [
          'We doen geen onbewezen claims over duurzaamheid, carbonkwaliteit, toernooigoedkeuring, medische voordelen, schoenveiligheid of impactbescherming. Zulke claims gebruiken we alleen met betrouwbare leverancier-, norm- of fabrikantdocumentatie.'
        ]
      }
    ]
  },
  {
    slug: '/productveiligheid/',
    title: 'Productveiligheid',
    navLabel: 'Productveiligheid',
    summary: 'Veilig gebruik, waarschuwingen en meldroute voor pickleballproducten.',
    updated,
    sections: [
      {
        heading: 'Onze veiligheidsaanpak',
        body: [
          'We verkopen consumentenproducten pas wanneer de leverancier, distributeur of fabrikant voldoende productinformatie kan leveren. Voor productveiligheid letten we op traceerbaarheid, verantwoordelijke economische operator, waarschuwingen, handleidingen, materiaalinformatie en recall-route.'
        ]
      },
      {
        heading: 'Veilig gebruik',
        bullets: [
          'Gebruik paddles, ballen, netten, court lines, grips, tassen en accessoires alleen voor pickleball en volgens de meegeleverde informatie.',
          'Controleer netsystemen en lijnen op stabiliteit, struikelgevaar, scherpe delen, slijtage, windbelasting en geschiktheid voor de ondergrond.',
          'Ruim losse onderdelen na gebruik op en gebruik beschadigde producten niet meer.',
          'Laat kinderen alleen spelen onder passende begeleiding.',
          'Gebruik schoenen, brillen of bescherming niet als medische garantie tegen letsel.'
        ]
      },
      {
        heading: 'Productinformatie per SKU',
        body: [
          'Elke verkoopbare SKU moet voldoende identificatie, leverancier/importeurinformatie, waarschuwingen, gebruiksinformatie en productspecifieke veiligheidsnotities hebben. Producten zonder voldoende informatie blijven geblokkeerd voor checkout.'
        ]
      },
      {
        heading: 'Meld een veiligheidsprobleem',
        body: [
          `Meld een mogelijk onveilig product direct via ${operatorDetails.email}. Stuur ordernummer, productnaam, foto's, batch- of labelinformatie en een beschrijving van wat er gebeurde. We beoordelen de melding met de leverancier en nemen waar nodig maatregelen, zoals waarschuwing, vervanging, verkoopstop, terugroepactie of melding aan bevoegde instanties.`
        ]
      },
      {
        heading: 'Geen ongeautoriseerde claims of assets',
        body: [
          'We gebruiken geen merklogo, productfoto, certificering, keurmerk, toernooigoedkeuring of veiligheidsclaim zonder passende toestemming of bewijs. Dit voorkomt verwarring en houdt de productinformatie betrouwbaar.'
        ]
      }
    ]
  },
  {
    slug: '/herroepingsformulier/',
    title: 'Modelformulier voor herroeping',
    navLabel: 'Herroepingsformulier',
    summary: 'Voorbeeldtekst om een online consumentenkoop te herroepen.',
    updated,
    sections: [
      {
        heading: 'Zo gebruik je dit formulier',
        body: [
          `Je mag dit formulier gebruiken, maar dat hoeft niet. Je kunt ook via /retour-aanmelden/ of per e-mail naar ${operatorDetails.email} duidelijk laten weten dat je de koop wilt herroepen.`,
          'Stuur je melding binnen de bedenktijd. Daarna stuur je het product binnen 14 dagen terug volgens de retourinstructie.'
        ]
      },
      {
        heading: 'Formulier',
        bullets: [
          `Aan: ${operatorDetails.legalOwner}, ${operatorDetails.returnAddress}, ${operatorDetails.email}`,
          'Ik deel u hierbij mee dat ik onze overeenkomst betreffende de verkoop van de volgende goederen herroep:',
          'Besteld op / ontvangen op:',
          'Naam consument:',
          'Adres consument:',
          'E-mailadres:',
          'Ordernummer:',
          'Datum:',
          'Handtekening consument, alleen wanneer dit formulier op papier wordt ingediend:'
        ]
      }
    ]
  },
  {
    slug: '/contact/',
    title: 'Contact en bedrijfsgegevens',
    navLabel: 'Contact',
    summary: 'Contactgegevens, bedrijfsgegevens en servicevragen voor ProPickleball.nl.',
    updated,
    sections: [
      {
        heading: 'Bedrijfsgegevens',
        bullets: [
          `${operatorDetails.brand}`,
          `${operatorDetails.legalOwner} / ${operatorDetails.operatingGroup}`,
          operatorDetails.address,
          `KVK: ${operatorDetails.kvk}`,
          `BTW-id: ${operatorDetails.vat}`,
          `E-mail: ${operatorDetails.email}`,
          `Telefoon/WhatsApp: ${operatorDetails.phone}`
        ]
      },
      {
        heading: 'Waarvoor kun je ons mailen?',
        bullets: [
          'Advies over startersets, paddles, ballen, netten en accessoires.',
          'Offertes voor clubs, scholen, clinics en verenigingen.',
          'Vragen over verzending, retouren, garantie, privacy, cookies en productveiligheid.',
          'Leverancier-, merk- of distributiecontact voor de NL/BE markt.'
        ]
      },
      {
        heading: 'Servicebelofte',
        body: [
          'We communiceren liever duidelijk dan te snel. Als een antwoord afhankelijk is van leverancier, vervoerder of productveiligheidsinformatie, dan zeggen we dat erbij en geven we een realistische vervolgstap.'
        ]
      }
    ]
  }
];

export const requiredLegalSlugs = legalPages.map((page) => page.slug);

export function normalizePath(pathname: string): string {
  if (pathname === '' || pathname === '/' || pathname === '/index.html') return '/';
  return pathname.endsWith('/') ? pathname : `${pathname}/`;
}

export function findLegalPage(pathname: string): LegalPage | null {
  const normalized = normalizePath(pathname);
  return legalPages.find((page) => page.slug === normalized) ?? null;
}
